import { createEntityAdapter } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import { decamelize } from 'humps';

import { parseFiltersData, removeEmptyAndDefaultFilters } from 'services/contacts/filters';
import { networkBaseQueryV2 } from '../queries';
import { mapCompanyDetailToV1, mapContactsV2ToV1 } from './formatter';
import deserialize from '../../helpers/getroPropertyMapper';

export const itemsAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

const itemsSelector = itemsAdapter.getSelectors();

export const contactsV2Api = createApi({
  reducerPath: 'contactsV2Api',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['importers', 'contacts', 'shared-lists-contacts'],
  endpoints: (builder) => ({
    getContactById: builder.query({
      query: (id) => ({
        url: `/contacts/${id}`,
      }),
      transformResponse: mapContactsV2ToV1,
      providesTags: ['contact'],
    }),
    getCompanyDetailByContactId: builder.query({
      query: (id) => ({
        url: `/contacts/${id}`,
      }),
      transformResponse: mapCompanyDetailToV1,
      providesTags: ['contact'],
    }),
    getContacts: builder.query({
      query: ({
        filters = {},
        listId,
        sortBy,
        sortDirection,
        listKind,
        contextualizedToSharedListId,
        includeListsCustomFields,
        ...rest
      }) => {
        const cleanedFilters = removeEmptyAndDefaultFilters(filters);
        const parsedFilters = parseFiltersData(cleanedFilters, true);

        if (listId) {
          if (!parsedFilters.sharedListIds) {
            parsedFilters.sharedListIds = [];
          }
          parsedFilters.sharedListIds.push(listId);
        }

        return {
          url: '/contacts/search',
          method: 'POST',
          data: {
            ...parsedFilters,
            ...(listId ? { sharedListId: listId } : undefined),
            ...(listId || includeListsCustomFields ? { includeListsCustomFields: true } : undefined),
            ...(sortBy ? { sortBy: decamelize(sortBy) } : undefined),
            ...(sortDirection ? { sortDirection: decamelize(sortDirection) } : undefined),

            ...(listKind !== 'companies' && contextualizedToSharedListId
              ? { contextualizedToSharedListId }
              : undefined),
            ...rest,
          },
        };
      },
      transformResponse: (response, _, arg) => ({
        meta: response.meta,
        page: arg.page,
        ...itemsAdapter.addMany(itemsAdapter.getInitialState(), mapContactsV2ToV1(response)),
      }),
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const queryFilters = queryArgs?.filters ? { ...queryArgs?.filters } : {};

        if (queryArgs?.sortBy) {
          queryFilters.sortBy = queryArgs?.sortBy;
        }
        if (queryArgs?.sortDirection) {
          queryFilters.sortDirection = queryArgs?.sortDirection;
        }

        const filters = queryString.stringify(parseFiltersData(queryFilters, true), {
          arrayFormat: 'bracket',
        });

        return queryArgs?.listId
          ? { listId: queryArgs?.listId?.toString(), filters: queryFilters }
          : `${endpointName}(${filters})`;
      },
      merge: (currentState, incomingState) => {
        if (incomingState.meta.queryTotal === 0) {
          itemsAdapter.removeAll(currentState);
        } else if (currentState.page < incomingState.page) {
          itemsAdapter.setMany(currentState, itemsSelector.selectAll(incomingState));
        } else {
          itemsAdapter.removeAll(currentState);
          itemsAdapter.addMany(currentState, itemsSelector.selectAll(incomingState));
        }

        Object.assign(currentState, {
          meta: incomingState.meta,
        });
      },
      providesTags: (data, items, args) => {
        if (args.board) {
          return ['contacts-board'];
        }

        if (args.listId) {
          return ['shared-lists-contacts', { type: 'contacts', id: args.listId }];
        }

        return ['contacts'];
      },
    }),
    getImporters: builder.query({
      query: () => ({
        url: '/contacts/importers',
      }),
      providesTags: ['importers'],
      transformResponse: (response) => {
        let importers = deserialize(response);
        importers = importers.sort((a, b) => a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()));

        return {
          importers,
        };
      },
    }),
    importContactsFromCsv: builder.mutation({
      query: ({ importData, fileName }) => ({
        url: '/contacts/import',
        method: 'post',
        data: {
          ...importData,
          fileName,
        },
      }),
    }),
    searchWithAi: builder.query({
      query: ({ query }) => ({
        url: `/contacts/search?contact_prompt=${query}`,
        method: 'get',
      }),
      async onQueryStarted({ queryParams }, { dispatch, queryFulfilled }) {
        const changeLoading = (isLoading = true) => {
          dispatch(
            contactsV2Api.util.updateQueryData('getContacts', queryParams, (draft) => {
              Object.assign(draft, {
                ...draft,
                meta: { ...draft.meta, isLoadingAI: isLoading },
              });
            }),
          );
        };

        try {
          changeLoading(true);

          const { data } = await queryFulfilled;

          if (Object.keys(data?.filters || {}).length > 0) {
            contactsV2Api.util.selectInvalidatedBy(['getContacts']);
            dispatch(
              contactsV2Api.util.updateQueryData(
                'getContacts',
                queryParams.listId ? { listId: queryParams.listId } : queryParams,
                (draft) => {
                  Object.assign(draft, {
                    meta: data.result.meta,
                    page: 1,
                    ...itemsAdapter.addMany(draft, mapContactsV2ToV1(data.result)),
                  });
                },
              ),
            );
          }

          changeLoading(false);
        } catch {
          changeLoading(false);
        }
      },
    }),
    getContactResume: builder.query({
      query: ({ id }) => ({
        url: `/contacts/${id}/resume`,
      }),
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactByIdQuery,
  useGetCompanyDetailByContactIdQuery,
  useLazyGetContactByIdQuery,
  useLazyGetContactsQuery,
  useLazyGetImportersQuery,
  useImportContactsFromCsvMutation,
  useGetImportersQuery,
  useLazySearchWithAiQuery,
  useGetContactResumeQuery,
  useLazyGetContactResumeQuery,
} = contactsV2Api;

/** Cache */

export const updateAllContactsCache = ({ state, dispatch, getChanges }) => {
  const cacheEntries = state.contactsV2Api.queries;

  return Object.entries(cacheEntries)
    .map(([, queryData]) => {
      if (queryData?.endpointName === 'getContacts' && queryData?.status === 'fulfilled') {
        dispatch(
          contactsV2Api.util.updateQueryData('getContacts', queryData.originalArgs, (draft) => {
            const allContacts = itemsSelector.selectAll(draft);
            const updates = allContacts.map((contact) => ({
              id: contact.id,
              changes: getChanges(contact),
            }));

            if (updates.length > 0) {
              itemsAdapter.updateMany(draft, updates);
            }
          }),
        );
      }

      return null;
    })
    .filter(Boolean);
};
